const STABLE_FNOL_API_URL =
  'https://fnol-frontend-api-650747488835.us-central1.run.app';

const LOCAL_FNOL_API_URL = 'http://localhost:8001';
const LOCAL_RETRY_COOLDOWN_MS = 60000;
const failedBaseRetryAt = new Map();

const normalizeBase = (value) => String(value || '').trim().replace(/\/$/, '');
const isLocalFnolApiBase = (base) => {
  try {
    return ['localhost', '127.0.0.1'].includes(new URL(base).hostname);
  } catch {
    return false;
  }
};
const shouldSkipBase = (base) =>
  isLocalFnolApiBase(base) && Date.now() < (failedBaseRetryAt.get(base) || 0);
const rememberBaseFailure = (base) => {
  if (isLocalFnolApiBase(base)) {
    failedBaseRetryAt.set(base, Date.now() + LOCAL_RETRY_COOLDOWN_MS);
  }
};
const clearBaseFailure = (base) => failedBaseRetryAt.delete(base);

export const getFnolApiBaseCandidates = () => {
  const configuredUrl = normalizeBase(process.env.REACT_APP_FNOL_API_URL);
  const isLocalHost =
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const bases = [];

  if (isLocalHost) {
    bases.push(LOCAL_FNOL_API_URL);
    if (configuredUrl) {
      bases.push(configuredUrl);
    }
    bases.push(STABLE_FNOL_API_URL);
    return [...new Set(bases.filter(Boolean))];
  }

  if (configuredUrl) {
    bases.push(configuredUrl);
  }

  bases.push(STABLE_FNOL_API_URL);

  return [...new Set(bases.filter(Boolean))];
};

export const getPreferredFnolApiBase = () =>
  getFnolApiBaseCandidates()[0] || STABLE_FNOL_API_URL;

export const buildFnolApiUrl = (path, base = getPreferredFnolApiBase()) =>
  `${normalizeBase(base)}${path.startsWith('/') ? path : `/${path}`}`;

const getErrorMessage = async (response) => {
  try {
    const data = await response.clone().json();
    const detail = data.detail || data.details || data.error || data.message || '';
    return typeof detail === 'string' ? detail : JSON.stringify(detail);
  } catch {
    return '';
  }
};

export const fetchFirstFnolResponse = async (path, options = {}) => {
  let lastError = null;
  const targetPath = path.startsWith('/') ? path : `/${path}`;

  for (const base of getFnolApiBaseCandidates()) {
    if (shouldSkipBase(base)) continue;

    const { timeoutMs: _ignoredTimeout, ...fetchOptions } = options;
    const controller = fetchOptions.signal ? null : new AbortController();
    const timeoutMs = options.timeoutMs || (isLocalFnolApiBase(base) ? 4000 : 15000);
    const timer = controller
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;

    try {
      const response = await fetch(buildFnolApiUrl(targetPath, base), {
        ...fetchOptions,
        signal: fetchOptions.signal || controller.signal,
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response);
        throw new Error(errorMessage || `Request failed (${response.status})`);
      }

      clearBaseFailure(base);
      return response;
    } catch (error) {
      lastError = error;
      rememberBaseFailure(base);
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  throw lastError || new Error('Request failed');
};

const parseJsonPayload = async (response) => {
  const text = await response.text();
  if (!text.trim()) return null;

  try {
    return JSON.parse(text);
  } catch {
    throw new Error('API response was not JSON');
  }
};

const normalizeJsonPayload = (payload) => {
  if (typeof payload !== 'string') return payload;

  const trimmed = payload.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
    return payload;
  }

  try {
    return normalizeJsonPayload(JSON.parse(trimmed));
  } catch {
    return payload;
  }
};

export const fetchFirstFnolJson = async (path, options = {}) => {
  let lastError = null;
  const targetPath = path.startsWith('/') ? path : `/${path}`;

  for (const base of getFnolApiBaseCandidates()) {
    if (shouldSkipBase(base)) continue;

    const { timeoutMs: _ignoredTimeout, ...fetchOptions } = options;
    const controller = fetchOptions.signal ? null : new AbortController();
    const timeoutMs = options.timeoutMs || (isLocalFnolApiBase(base) ? 4000 : 15000);
    const timer = controller
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;

    try {
      const response = await fetch(buildFnolApiUrl(targetPath, base), {
        ...fetchOptions,
        signal: fetchOptions.signal || controller.signal,
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response);
        throw new Error(errorMessage || `Request failed (${response.status})`);
      }

      clearBaseFailure(base);
      return normalizeJsonPayload(await parseJsonPayload(response));
    } catch (error) {
      lastError = error;
      rememberBaseFailure(base);
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  throw lastError || new Error('Request failed');
};
